import { useState } from 'react';
import { useSelector } from 'react-redux';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { Button } from '@/components/Button';
import { ModalComponent } from '@/components/ModalComponent';
import { ModalHeader } from '@/components/ModalHeader';
import {
    EState,
    IAddress,
    selectAddressByIndex,
    setAddressByIndex,
} from '@/context/borrower';
import {
    EJourneys,
    selectInitialJourney,
    selectIsNZ,
    setModal,
} from '@/context/display';
import { RootState } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { LivingStatus } from '@/UserInput';
import { AddressState } from '@/UserInput/AddressState';
import { PostCode } from '@/UserInput/PostCode';
import { StreetName } from '@/UserInput/StreetName';
import { StreetNumber } from '@/UserInput/StreetNumber';
import { SuburbName } from '@/UserInput/SuburbName';
import { UnitNumber } from '@/UserInput/UnitNumber';

export const testId = 'AddressInformation';

export function AddressInformation(): JSX.Element {
    // ***** Constants *****
    const baseId = 'modal-addressDetails';
    // ***** Local State *****
    const [showErrors, setShowErrors] = useState<boolean>(false);
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const address = useSelector((state: RootState) =>
        selectAddressByIndex(state, 0)
    );
    const { ownershipType, streetNumber, streetName, suburb, state, postcode } =
        address;
    const initialJourney = useAppSelector(selectInitialJourney);
    const isNZ = useAppSelector(selectIsNZ);
    const isTestDrive = initialJourney === EJourneys.TEST_DRIVE;

    // Clear AU demo defaults for NZ partners on open
    // Run once per open by checking the current values
    if (isNZ) {
        const auStates: string[] = [
            EState.NSW,
            EState.VIC,
            EState.QLD,
            EState.ACT,
            EState.WA,
            EState.NT,
            EState.SA,
            EState.TAS,
        ] as unknown as string[];
        const looksLikeDefault =
            streetNumber === '12' ||
            streetNumber === '260' ||
            streetName === 'George St' ||
            streetName === 'Elizabeth St' ||
            suburb === 'Sydney' ||
            postcode === '2000' ||
            postcode === '2010' ||
            (state && auStates.includes(state as unknown as string));
        if (looksLikeDefault) {
            const cleared: IAddress = {
                ...address,
                unitNumber: undefined,
                streetNumber: undefined,
                streetName: undefined,
                suburb: undefined,
                state: undefined,
                postcode: undefined,
            };
            dispatch(setAddressByIndex({ index: 0, address: cleared }));
        }
    }

    // ***** Handlers *****
    function handleClick(): void {
        setShowErrors(true);
        if (!isComplete()) return;
        createOrUpdateBorrower();
        dispatch(setModal(null));
    }

    // ***** Helpers *****
    function isComplete(): boolean {
        if (!isTestDrive) {
            if (ownershipType === null) return false;
            if (ownershipType === undefined) return false;
        }

        if (streetNumber === '') return false;
        if (streetNumber === undefined) return false;

        if (streetName === '') return false;
        if (streetName === undefined) return false;

        if (suburb === '') return false;
        if (suburb === undefined) return false;

        if (state === null) return false;
        if (state === undefined) return false;

        if (postcode === '') return false;
        if (postcode === undefined) return false;
        if (postcode.toString()?.length !== 4) return false;

        return true;
    }

    // ***** Render *****
    return (
        <ModalComponent baseId={baseId} data-testid={testId}>
            <ModalHeader title='Address Details' baseId={baseId} />
            {!isTestDrive && (
                <LivingStatus baseId={baseId} showErrors={showErrors} />
            )}
            <div className='flex-center space-x-2'>
                <UnitNumber baseId={baseId} showErrors={false} />
                <StreetNumber baseId={baseId} showErrors={showErrors} />
            </div>
            <div className='flex-center space-x-2'>
                <StreetName baseId={baseId} showErrors={showErrors} />
                <SuburbName baseId={baseId} showErrors={showErrors} />
            </div>
            <div className='flex-center space-x-2'>
                <AddressState baseId={baseId} showErrors={showErrors} />
                <PostCode baseId={baseId} showErrors={showErrors} />
            </div>
            <div className='py-2 mx-16'>
                <Button onClick={handleClick} id={`${baseId}-button-close`}>
                    Close
                </Button>
            </div>
        </ModalComponent>
    );
}
